<template>
  <div v-if="eligibleForMUP" class="middle-container">
    <div class="col-xs-12">
      <b-headline type="h4" typography="h4" class="text-left" v-html="$t('title')" />
      <div v-if="invitees && invitees.length > 0" class="text-dark-grey mt-8">
        <div v-html="$t('inviteeEmailLabel')" />
        <div v-for="invitee in invitees" :key="invitee.userId" class="flex items-center w-full mt-8">
          <div class="flex-grow m:flex-grow-0 m:w-1/2">
            {{ invitee.nickname || $t("missingNickname") }}
          </div>
          <a
            :id="`mup-member-${invitee.userId}`"
            class="flex cursor-pointer items-center ml-8"
            @click="triggerRemoveUserModal(invitee)"
          >
            <sprite id="app/delete" class="h-4 w-4" />
            <span class="ml-2">
              {{ $t("remove", { email: invitee.email }) }}
            </span>
          </a>
        </div>
      </div>
      <div v-else-if="hasB2cOrg && inviteLink">
        <p v-html="$t('inviteLinkLabel')" class="mb-8 max-w-md" />
        <div class="flex items-center">
          <input-text disabled :value="inviteLink" name="mup-invite-link" class="flex-grow m:flex-grow-0 m:w-1/2" />
          <a class="cursor-pointer ml-8" @click="copyToClipboard">
            {{ $t("copy") }}
          </a>
        </div>
      </div>
      <div v-else>
        <div class="text-dark-grey mt-8">
          <p v-html="$t('generateLabel')" class="max-w-md" />
        </div>
        <div class="l:flex">
          <input-text
            v-if="!userName"
            placeholder="eg. Julia"
            class="max-w-xs"
            name="inviter_name"
            :required="true"
            :label="$t('Name')"
            @input="removeInvalidChars"
            v-model.trim="inviterName"
          />
          <b-button
            @click="createOwnerOrg"
            :disabled="!inviterName.length && !userName"
            variant="product-interaction"
            name="generate_invite_link"
            :class="{ 'l:ml-4': !userName }"
            class="m:w-44 inline-block mt-4 l:mt-7"
          >
            {{ $t("Generate link") }}
          </b-button>
        </div>
      </div>
    </div>

    <remove-mup-member-modal :user-email="memberToRemove && memberToRemove.email" @submit="removeUser" />

    <legacy-divider-line />
  </div>
</template>

<script>
import Sprite from "@/shared/sprite"
import BHeadline from "@/component-library/b-headline"
import { mapState } from "vuex"
import { writeTextToClipboard } from "@/utils/clipboard"
import LegacyDividerLine from "@/features/settings/components/legacy-divider-line"
import RemoveMupMemberModal from "@/features/settings/components/modals/remove-mup-member-modal"
import InputText from "@/shared/form/input-text"
import BButton from "@/component-library/b-button"
import { filterEmoticons } from "@/services/filter-string"

export default {
  name: "MupOwnerManagement",
  components: {
    RemoveMupMemberModal,
    Sprite,
    LegacyDividerLine,
    BHeadline,
    InputText,
    BButton
  },
  data() {
    return {
      inviterName: ""
    }
  },
  computed: {
    ...mapState("multiUserPlanManagement", [
      "hasB2cOrg",
      "invitees",
      "inviteLink",
      "isFull",
      "memberToRemove",
      "eligibleForMUP",
      "userName"
    ])
  },
  mounted() {
    this.fetchOwnerOrg()
  },
  methods: {
    removeInvalidChars() {
      this.inviterName = filterEmoticons(this.inviterName)
    },
    createOwnerOrg() {
      this.$store.dispatch("multiUserPlanManagement/createOwnerOrg", this.userName || this.inviterName)
    },
    fetchOwnerOrg() {
      this.$store.dispatch("multiUserPlanManagement/fetchOwnerOrg")
    },
    triggerRemoveUserModal(member) {
      this.$store.dispatch("multiUserPlanManagement/triggerRemoveUserModal", member)
    },
    removeUser() {
      this.$store.dispatch("multiUserPlanManagement/removeUser")
    },
    copyToClipboard() {
      writeTextToClipboard(this.inviteLink)
      this.$store.dispatch("toaster/push", {
        id: "copySuccess",
        message: this.$t("copied"),
        type: "success"
      })
      setTimeout(() => this.$store.dispatch("toaster/pop", "copySuccess"), 3000)
    }
  },
  i18n: {
    messages: {
      en: {
        title: "Plan Sharing",
        inviteeEmailLabel: "Your shared account seat is active and currently used by:",
        inviteLinkLabel:
          "Your plan comes with a free guest account to give to someone.<br /> <br /> To add the, to your plan, click the invite button below to copy the link and send it to them. They will then be able to create their own account on your plan.",
        generateLabel:
          "All Blinkist plans come with one invitation to add a friend and share all that Blinkist has to offer! <br/> Enter your name to receive an invite link you can use to invite someone to share your Premium plan with Blinkist Connect.",
        remove: "Remove",
        copy: "Copy invite link",
        copied: "Link copied to clipboard",
        inviteButton: "Go to invite link",
        MUPerror: "Something went wrong",
        missingNickname: "(Your friend has not entered their name yet)"
      },
      de: {
        title: "Teil dein Abo",
        inviteeEmailLabel: "Der Extra-Platz in deinem Abo ist aktiv und momentan belegt von:",
        inviteLinkLabel:
          "In deinem Abo ist ein Extra-Account zum Teilen enthalten. Um jemanden zu deinem Abo einzuladen, klicke einfach unten auf den Button, um den Einladungslink zu kopieren und zu versenden. Die eingeladene Person kann dann ihren eigenen Account innerhalb deines Abos erstellen.",
        generateLabel:
          "Dein Abo macht ab jetzt doppelt so viel Spaß, denn du kannst eine:n Freund:in einladen und all deine Highlights teilen. <br /> Einfach deinen Namen eingeben und du erhältst für dein Abo einen Connect-Einladungslink, den du mit einer Person deiner Wahl teilen kannst.",
        remove: "Entfernen",
        copy: "Link kopieren",
        copied: "Link wurde in die Zwischenablage kopiert",
        inviteButton: "Zum Einladungslink",
        MUPerror: "Bei der Erstellung eines Einladungslinks ist ein Fehler aufgetreten",
        missingNickname: "(Die eingeladene Person hat ihren Namen noch nicht eingetragen)"
      }
    }
  }
}
</script>
